// contexts/AssistantActionContext.tsx
'use client';

import React, { createContext, useContext, useCallback, useState, ReactNode } from 'react';
import { useAssistantActionListener, AssistantAction, dispatchAssistantActionResult } from '@/lib/assistant-events';
import { useToast } from '@/hooks/useToast';

interface AssistantActionContextType {
  registerActionHandler: (actionName: string, handler: (payload: any, action: AssistantAction) => void) => void;
  unregisterActionHandler: (actionName: string) => void;
  triggerAssistant: (mode?: string, initialMessage?: string) => void;
}

const AssistantActionContext = createContext<AssistantActionContextType | undefined>(undefined);

export const useAssistantAction = () => {
  const context = useContext(AssistantActionContext);
  if (!context) {
    throw new Error('useAssistantAction must be used within an AssistantActionProvider');
  }
  return context;
};

export const AssistantActionProvider = ({ children }: { children: ReactNode }) => {
  const [handlers, setHandlers] = useState<Record<string, (payload: any, action: AssistantAction) => void>>({});
  const { addToast } = useToast();

  const registerActionHandler = useCallback((actionName: string, handler: (payload: any, action: AssistantAction) => void) => {
    setHandlers(prev => ({ ...prev, [actionName]: handler }));
  }, []);

  const unregisterActionHandler = useCallback((actionName: string) => {
    setHandlers(prev => {
      const newHandlers = { ...prev };
      delete newHandlers[actionName];
      return newHandlers;
    });
  }, []);

  const handleAction = useCallback((action: AssistantAction) => {
    const handler = handlers[action.action];
    if (handler) {
      handler(action.payload, action);
    } else if (action.action === 'navigate' && action.payload?.path) {
      // Default handler for navigation
      const path = action.payload.path;
      const target = action.payload.target;

      if (!target || target === '_self') {
        window.location.href = path;
      } else {
        // Use named target if provided (e.g., 'content-strategy'), or '_blank' for 'new_tab'
        const windowTarget = target === 'new_tab' ? '_blank' : target;
        window.open(path, windowTarget);

        // Dispatch fallback link for new tab/window navigations
        // This adds a clickable link below the message in case popup was blocked
        // NOTE: We use setTimeout to avoid race condition where the message hasn't been added to state yet
        if (action.messageId) {
          // Generate a friendly label based on the path
          let label = 'Open Link';
          if (path.includes('content-strategy')) {
            label = 'Set up Content Strategy';
          } else if (path.includes('articles/edit')) {
            label = 'Open Article';
          }

          setTimeout(() => {
            dispatchAssistantActionResult(action.messageId!, {
              type: 'link',
              url: path,
              label,
              target: windowTarget
            });
          }, 100);
        }
      }
    } else {
      // Unhandled event catcher
      console.warn(`No handler registered for action: ${action.action}`);
      addToast(`Action "${action.action}" is not supported on this page.`, 'error');
    }
  }, [handlers, addToast]);

  useAssistantActionListener(handleAction);

  const triggerAssistant = useCallback((mode?: string, initialMessage?: string) => {
    window.dispatchEvent(new CustomEvent('open-assistant', {
      detail: { mode, initialMessage }
    }));
  }, []);

  const value = { registerActionHandler, unregisterActionHandler, triggerAssistant };

  return (
    <AssistantActionContext.Provider value={value}>
      {children}
    </AssistantActionContext.Provider>
  );
};